import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../lib/firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

/* ---------------- mobile select helpers ---------------- */
function useIsMobile(breakpoint = 520) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= breakpoint;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function MobileSheetSelect({
  label,
  value,
  onChange,
  options, // [{value,label}]
  disabled = false,
  placeholder = "Select…",
}) {
  const [open, setOpen] = useState(false);

  const selectedLabel =
    options.find((o) => String(o.value) === String(value))?.label || "";

  const close = () => setOpen(false);

  return (
    <div style={{ display: "grid", gap: 6 }}>
      {label ? <div style={S.label}>{label}</div> : null}

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        style={{
          ...S.input,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: disabled ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.95)",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <span style={{ color: selectedLabel ? "#111827" : "rgba(17,24,39,0.55)" }}>
          {selectedLabel || placeholder}
        </span>
        <span style={{ fontWeight: 1000, opacity: 0.85 }}>▾</span>
      </button>

      {open ? (
        <div onClick={close} style={S.overlay}>
          <div onClick={(e) => e.stopPropagation()} style={S.sheet}>
            <div style={S.sheetTop}>
              <div style={S.sheetTitle}>{label || "Select"}</div>
              <button type="button" onClick={close} style={S.sheetClose}>
                ✕
              </button>
            </div>

            <div style={S.sheetBody}>
              <div style={{ display: "grid", gap: 10 }}>
                {options.map((opt) => {
                  const active = String(opt.value) === String(value);
                  return (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => {
                        onChange(opt.value);
                        close();
                      }}
                      style={{
                        ...S.pickRow,
                        ...(active ? S.pickRowActive : null),
                      }}
                    >
                      <div style={{ fontWeight: 950 }}>{opt.label}</div>
                      {active ? <div style={{ fontWeight: 1000 }}>✓</div> : <div />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function ReportScreen() {
  const nav = useNavigate();
  const loc = useLocation();

  // optional context from chat (if you navigate with state)
  const chatCtx = loc.state || {};
  const prefillType = safe(chatCtx?.type) || ""; // optional if you pass it
  const prefillTarget = safe(chatCtx?.targetId) || safe(chatCtx?.messageId) || "";
  const prefillReason = safe(chatCtx?.reason) || "";
  const prefillDetails = safe(chatCtx?.details) || safe(chatCtx?.messageText) || "";

  const isMobile = useIsMobile(520);

  const [me, setMe] = useState(null);

  const [type, setType] = useState(prefillType || "BUG");
  const [reason, setReason] = useState(prefillReason);
  const [targetId, setTargetId] = useState(prefillTarget);
  const [details, setDetails] = useState(prefillDetails);

  const [sending, setSending] = useState(false);
  const [done, setDone] = useState("");

  // ✅ FIX: currentUser can be null on first render
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        nav("/login", { replace: true });
        return;
      }
      setMe({ uid: u.uid, email: safe(u.email) });
    });
    return () => unsub();
  }, [nav]);

  const typeOptions = useMemo(
    () => [
      { value: "BUG", label: "Bug" },
      { value: "SCAM", label: "Scam / Fraud" },
      { value: "ABUSE", label: "Abuse" },
      { value: "OTHER", label: "Other" },
    ],
    []
  );

  async function submit() {
    setDone("");

    const r = safe(reason);
    const d = safe(details);
    const tId = safe(targetId);

    if (!r) {
      setDone("Please add a short reason (title).");
      return;
    }
    if (!d) {
      setDone("Please describe the issue.");
      return;
    }
    if (!me?.uid) {
      setDone("You must be logged in.");
      return;
    }

    try {
      setSending(true);

      await addDoc(collection(db, "reports"), {
        uid: me.uid,
        email: me.email,

        // ✅ matches AdminReportsScreen display
        type: safe(type) || "OTHER",
        reason: r,
        targetId: tId, // optional

        details: d,

        status: "OPEN",

        // ✅ admin uses/updates these
        assignedTo: "",
        resolvedBy: "",
        resolutionNote: "",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setReason("");
      setTargetId("");
      setDetails("");
      setType("BUG");
      setDone("Report submitted ✅");
    } catch (e) {
      console.error(e);
      setDone(e?.message ? `Failed ❌ ${e.message}` : "Failed to submit ❌ Try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={S.page}>
      <div style={S.shell}>
        {/* Header that matches chat vibe */}
        <div style={S.header}>
          <button type="button" onClick={() => nav(-1)} style={S.backBtn} title="Back">
            ←
          </button>

          <div style={{ minWidth: 0 }}>
            <div style={S.hTitle}>Report</div>
            <div style={S.hSub}>Tell us what happened</div>
          </div>

          <div />
        </div>

        <div style={S.card}>
          {/* Type */}
          {isMobile ? (
            <MobileSheetSelect
              label="Type"
              value={type}
              onChange={(v) => setType(String(v))}
              options={typeOptions}
              placeholder="Select type"
              disabled={sending}
            />
          ) : (
            <>
              <div style={S.label}>Type</div>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={S.input}
                disabled={sending}
              >
                <option value="BUG">Bug</option>
                <option value="SCAM">Scam / Fraud</option>
                <option value="ABUSE">Abuse</option>
                <option value="OTHER">Other</option>
              </select>
            </>
          )}

          <div style={{ height: 12 }} />

          <div style={S.label}>Reason (short title)</div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="E.g. Harassment / Scam / App issue"
            style={S.input}
            disabled={sending}
          />

          <div style={{ height: 12 }} />

          <div style={S.label}>Target ID (optional)</div>
          <input
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            placeholder="User ID / Match ID / Message ID"
            style={S.input}
            disabled={sending}
          />

          <div style={{ height: 12 }} />

          <div style={S.label}>Details</div>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Explain what happened…"
            style={S.textarea}
            disabled={sending}
          />

          {done ? <div style={S.note}>{done}</div> : null}

          <button type="button" onClick={submit} style={S.btn} disabled={sending}>
            {sending ? "Submitting…" : "Submit report"}
          </button>

          <div style={S.smallTip}>We’ll review it as soon as possible.</div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { width: "100%", boxSizing: "border-box" },

  shell: { width: "100%" },

  // ✅ match your app "glow + soft" background
  // If AppLayout already provides background, this still blends nicely.
  // If not, this gives the same modern vibe as chat screens.
  pageInner: {},

  header: {
    display: "grid",
    gridTemplateColumns: "44px 1fr 44px",
    alignItems: "center",
    gap: 10,
    padding: "6px 0",
    marginBottom: 10,
  },

  backBtn: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    fontSize: 20,
    display: "grid",
    placeItems: "center",
    boxShadow: "0 12px 22px rgba(17,24,39,0.08)",
    fontWeight: 950,
  },

  hTitle: {
    fontWeight: 950,
    fontSize: 18,
    letterSpacing: -0.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  hSub: {
    fontWeight: 800,
    opacity: 0.65,
    fontSize: 12,
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  // ✅ same glass card feeling as chatCard
  card: {
    width: "100%",
    borderRadius: 22,
    border: "1px solid rgba(17,24,39,0.06)",
    background: "rgba(255,255,255,0.75)",
    boxShadow: "0 18px 40px rgba(17,24,39,0.08)",
    overflow: "hidden",
    padding: 14,
    backdropFilter: "blur(10px)",
  },

  label: { fontWeight: 950, color: "#111827", marginBottom: 6, fontSize: 12 },

  input: {
    width: "100%",
    height: 44,
    padding: "0 14px",
    borderRadius: 16,
    border: "1px solid rgba(17,24,39,0.10)",
    outline: "none",
    fontWeight: 850,
    background: "rgba(255,255,255,0.95)",
  },

  textarea: {
    width: "100%",
    minHeight: 140,
    padding: "12px 14px",
    borderRadius: 16,
    border: "1px solid rgba(17,24,39,0.10)",
    outline: "none",
    fontWeight: 850,
    background: "rgba(255,255,255,0.95)",
    resize: "vertical",
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  note: {
    marginTop: 10,
    padding: 10,
    borderRadius: 16,
    border: "1px solid rgba(17,24,39,0.08)",
    background: "rgba(2,6,23,0.03)",
    fontWeight: 900,
    color: "rgba(17,24,39,0.80)",
  },

  btn: {
    marginTop: 12,
    width: "100%",
    height: 44,
    borderRadius: 14,
    border: "none",
    cursor: "pointer",
    fontWeight: 950,
    color: "#fff",
    background: "linear-gradient(90deg, rgba(59,130,246,0.95), rgba(99,102,241,0.95))",
    boxShadow: "0 16px 24px rgba(59,130,246,0.18)",
  },

  smallTip: {
    marginTop: 10,
    fontWeight: 850,
    fontSize: 12,
    color: "rgba(17,24,39,0.55)",
  },

  /* --------- MobileSheetSelect styles to match chat overlay --------- */
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,0.35)",
    backdropFilter: "blur(4px)",
    zIndex: 9999,
    display: "grid",
    placeItems: "end center",
    padding: 14,
    boxSizing: "border-box",
  },

  sheet: {
    width: "100%",
    maxWidth: 520,
    borderRadius: 18,
    background: "rgba(255,255,255,0.98)",
    border: "1px solid rgba(17,24,39,0.10)",
    boxShadow: "0 22px 60px rgba(17,24,39,0.22)",
    overflow: "hidden",
  },

  sheetTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    borderBottom: "1px solid rgba(17,24,39,0.08)",
  },

  sheetTitle: { fontWeight: 1000 },

  sheetClose: {
    width: 38,
    height: 38,
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 1000,
  },

  sheetBody: {
    maxHeight: "56vh",
    overflow: "auto",
    padding: 12,
  },

  pickRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "#fff",
    cursor: "pointer",
    textAlign: "left",
    fontWeight: 900,
  },

  pickRowActive: {
    background: "linear-gradient(90deg, rgba(59,130,246,0.10), rgba(99,102,241,0.10))",
    border: "1px solid rgba(59,130,246,0.22)",
  },
};
